/**
 * ReviewContext - Provides per-item evidence metadata and extracted values to Field components.
 *
 * Set per array item in output components (e.g., DrugOutputField) so nested
 * Field components can navigate the metadata tree to find their own evidence
 * and extracted values without prop threading through sections.
 */

import React, { createContext, useContext, useMemo } from 'react';

import type { FieldEvidence, HeardSeek } from '../types';

interface ReviewContextValue {
  /** Per-item metadata from output_data (navigable tree with .evidence leaves) */
  outputItemMeta?: Record<string, any>;
  outputSourceCallId?: string;
  /** Per-item metadata from extracted_data */
  extractedItemMeta?: Record<string, any>;
  extractedSourceCallId?: string;
  /** Per-item extracted VALUES from extracted_data (mirrors data structure) */
  extractedItemData?: Record<string, any>;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  isHumanAudit?: boolean;
  onAcceptExtracted?: (fieldPath: string, value: any, fieldLabel: string) => void;
  /** The ID of the call currently being viewed — used to filter sparkles to current-call extractions only */
  currentCallId?: string;
}

const ReviewContext = createContext<ReviewContextValue>({});

interface ReviewProviderProps extends ReviewContextValue {
  children: React.ReactNode;
}

export const ReviewProvider: React.FC<ReviewProviderProps> = ({
  outputItemMeta,
  outputSourceCallId,
  extractedItemMeta,
  extractedSourceCallId,
  extractedItemData,
  onNavigateToCall,
  isHumanAudit,
  onAcceptExtracted,
  currentCallId,
  children,
}) => {
  const value = useMemo(
    () => ({
      outputItemMeta,
      outputSourceCallId,
      extractedItemMeta,
      extractedSourceCallId,
      extractedItemData,
      onNavigateToCall,
      isHumanAudit,
      onAcceptExtracted,
      currentCallId,
    }),
    [
      outputItemMeta,
      outputSourceCallId,
      extractedItemMeta,
      extractedSourceCallId,
      extractedItemData,
      onNavigateToCall,
      isHumanAudit,
      onAcceptExtracted,
      currentCallId,
    ],
  );

  return <ReviewContext.Provider value={value}>{children}</ReviewContext.Provider>;
};

export const useReviewContext = () => useContext(ReviewContext);

/**
 * Navigate item metadata to find evidence for a specific field.
 *
 * pathPrefix (from FieldPathContext) includes the entity type (e.g., "drug.coverage").
 * The entity prefix is stripped since we're already inside the item's metadata.
 * Then we navigate the remaining path + fieldId to find the evidence array.
 *
 * Example: pathPrefix="drug.coverage", fieldId="coverage_status"
 *   → strip "drug" → ["coverage"]
 *   → navigate: itemMeta.coverage.coverage_status.evidence
 */
export function getFieldEvidence(
  itemMeta: Record<string, any> | undefined,
  pathPrefix: string,
  fieldId: string | undefined,
): FieldEvidence[] | undefined {
  if (!itemMeta || !fieldId) return undefined;
  const segments = pathPrefix.split('.').slice(1);
  let node: any = itemMeta;
  for (const seg of segments) {
    node = node?.[seg];
  }
  return node?.[fieldId]?.evidence;
}

/**
 * Same nested navigation as getFieldEvidence, but returns the per-field "heard" seek
 * descriptor (set when a field was marked heard during the call) — read by the dedicated
 * HeardSeekButton, independent of extraction evidence.
 */
export function getFieldHeardSeek(
  itemMeta: Record<string, any> | undefined,
  pathPrefix: string,
  fieldId: string | undefined,
): HeardSeek | undefined {
  if (!itemMeta || !fieldId) return undefined;
  const segments = pathPrefix.split('.').slice(1);
  let node: any = itemMeta;
  for (const seg of segments) {
    node = node?.[seg];
  }
  return node?.[fieldId]?.heardSeek;
}

/**
 * Navigate item extracted data to find the extracted value for a specific field.
 * Same path navigation as getFieldEvidence but returns the value directly.
 *
 * Example: pathPrefix="drug.coverage", fieldId="coverage_status"
 *   → navigate: itemData.coverage.coverage_status
 */
export function getFieldExtractedValue(
  itemData: Record<string, any> | undefined,
  pathPrefix: string,
  fieldId: string | undefined,
): any {
  if (!itemData || !fieldId) return undefined;
  const segments = pathPrefix.split('.').slice(1);
  let node: any = itemData;
  for (const seg of segments) {
    node = node?.[seg];
  }
  return node?.[fieldId];
}
